import { RouteAdmission, StoreAccessBoundary, SessionUnavailable } from "@/shared/access/StoreAccess";
import { useEffect, useState } from "react";
import { LoadingSkeleton } from "@a2z/ui";
import { shouldOpenQuickSetup } from "./needsQuickSetup";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { keys, getLocaleDirection } from "@/lib/i18n/portal";
import { Surface } from "@/shared/layout/Surface";
import { AppShell } from "@/shared/layout/AppShell";
import {
  SetupLauncher,
  useFloatingHelperOwner,
} from "@/shared/onboarding/components/SetupLauncher";
import { HelpLauncher } from "@/shared/help/components/HelpLauncher";
import { HelpDemoProvider } from "@/shared/help/HelpDemoContext";
import { SubscriptionRestrictedBanner } from "@/shared/layout/SubscriptionRestrictedBanner";
import { StoreWalletShortBanner } from "@/shared/layout/StoreWalletShortBanner";
import { RouteErrorBoundary } from "@/shared/error/RouteErrorBoundary";
import { sessionLossNotice } from "@/lib/auth/session-notice";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { NotificationsProvider } from "@/shared/notifications/NotificationsProvider";
import { FeatureFlagsProvider } from "@/shared/feature-flags/FeatureFlagsProvider";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "./locale";
import { RestaurantStoreTerminal } from "@/shared/layout/RestaurantStoreTerminal";

export function ProtectedLayout() {
  const locale = useLocale();
  const { storeSlug = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslations();
  const { data, isLoading, error, refetch } = useSessionQuery({ storeSlug });

  // Lifted out of HelpLauncher: the page tree needs to know a tour is
  // running so empty regions can show sample rows (`HelpDemoProvider`).
  // Declared ABOVE the early returns below — this component bails out for
  // login, store mismatch, loading, restaurant stores and onboarding, and a
  // hook after any of those changes the hook count between renders.
  const [helpOpen, setHelpOpen] = useState(false);
  // Which step the tour is on, so a page can put itself into the state that
  // step describes (see HelpDemoProvider).
  const [helpAnchor, setHelpAnchor] = useState<string | null>(null);

  // A member removed from the store while signed in lands on the login page
  // with the same sentence the login itself would show (B10), not a blank bounce.
  const loginTarget = `/${locale}/login${sessionLossNotice(error)}`;
  useEffect(() => {
    if (
      error instanceof ApiJsonClientError &&
      (error.status === 401 || error.status === 403)
    ) {
      navigate(loginTarget, { replace: true });
      return;
    }

    const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
    if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
      navigate(`/${locale}/${authenticatedStoreSlug}`, { replace: true });
    }
  }, [data?.seller?.storeSlug, error, locale, navigate, storeSlug, loginTarget]);

  if (
    error instanceof ApiJsonClientError &&
    (error.status === 401 || error.status === 403)
  ) {
    return <Navigate to={loginTarget} replace />;
  }

  const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
  if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
    return <Navigate to={`/${locale}/${authenticatedStoreSlug}`} replace />;
  }

  if (isLoading) {
    return (
      <Surface
        locale={locale}
        direction={getLocaleDirection(locale)}
        title={t(keys.common.loading)}
      >
        <div role="status" aria-busy="true">
          <span className="sr-only">{t(keys.common.loading)}</span>
          <LoadingSkeleton variant="card" rows={3} />
        </div>
      </Surface>
    );
  }

  // Only when there is nothing to show. A failed BACKGROUND refetch (an
  // access-revision or focus refetch) keeps the cached session, and with
  // it the shell and whatever the seller was typing.
  if (!data) return <SessionUnavailable locale={locale} retry={refetch} />;

  if (data?.mustChangePassword) {
    return <Navigate to={`/${locale}/change-password`} replace />;
  }

  // Decision D13 — a RESTAURANT store is administered only in apps/restaurant.
  // The check lives HERE, at the protected layout, rather than in the nav: a
  // hidden nav still leaves every seller route reachable by URL, so a deep link
  // to /{locale}/{slug}/orders would render the page and the portal would be
  // half-usable — exactly what the decision exists to prevent.
  if (data?.seller?.storeKind === "RESTAURANT") {
    return (
      <RestaurantStoreTerminal
        locale={locale}
        storeSlug={storeSlug}
        availableStores={data.availableStores ?? []}
      />
    );
  }

  const isDefaultEntry = location.pathname.replace(/\/$/, "") === `/${locale}/${storeSlug}`;
  const needsOnboarding = shouldOpenQuickSetup(data.overview, data.seller, isDefaultEntry);
  // The orders page intentionally renders with no top padding so its sticky
  // header can own that space (see OrdersPage). Without a nudge the wallet
  // banner sits flush against the chrome there, unlike every other page which
  // brings its own padding.
  const isOnOrders = /\/orders(\/|$)/.test(location.pathname);
  // The banner's whole job is to hand over a link to Payments. On Payments
  // itself it is a dead end pointing at the page you are already reading — and
  // on desktop it would push the full-height tab shell past the viewport.
  const isOnPayments = /\/payments(\/|$)/.test(location.pathname);

  if (needsOnboarding) {
    return <Navigate to={`/${locale}/${storeSlug}/quick-setup`} replace />;
  }

  return (
    <StoreAccessBoundary session={data} locale={locale} storeSlug={storeSlug} routeAdmission="shell">
    <FeatureFlagsProvider flags={data?.featureFlags}>
      <NotificationsProvider
        sellerId={data?.seller?.id}
        storeId={data?.seller?.storeId}
      >
        <AppShell locale={locale} storeSlug={storeSlug} loginUrl={`/${locale}/login`}>
          <SubscriptionRestrictedBanner storeSlug={storeSlug} />
          {isOnPayments ? null : (
            <StoreWalletShortBanner
              storeSlug={storeSlug}
              className={isOnOrders ? "mt-3" : undefined}
            />
          )}
          {/* Wraps the PAGE, so any region with an empty state can render a
              few sample rows while a help tour is running — a blank screen
              cannot be explained. */}
          <HelpDemoProvider active={helpOpen} anchor={helpAnchor}>
            <RouteErrorBoundary>
              <div key={location.pathname}>
                {/* Per-route admission INSIDE the shell: a denied route keeps
                    the nav and explains itself (StoreAccess.tsx). */}
                <RouteAdmission>
                  <Outlet />
                </RouteAdmission>
              </div>
            </RouteErrorBoundary>
          </HelpDemoProvider>
          {/* Mounted on the shell, not a page: the setup checklist used to live
              only on the dashboard, so navigating anywhere else lost it.
              ONE floating slot, two owners — "finish setting up" while steps
              remain, "explain this page" after. Two buttons in the same corner
              would be a choice nobody asked to make. */}
          {data.seller.access?.capabilities.onboarding ? <FloatingHelperSlot
            locale={locale}
            storeSlug={storeSlug}
            helpOpen={helpOpen}
            onHelpOpenChange={setHelpOpen}
            onHelpAnchorChange={setHelpAnchor}
          /> : null}
        </AppShell>
      </NotificationsProvider>
    </FeatureFlagsProvider>
    </StoreAccessBoundary>
  );
}

/** Setup while it has something to ask for; page help once it does not. */
function FloatingHelperSlot({
  locale,
  storeSlug,
  helpOpen,
  onHelpOpenChange,
  onHelpAnchorChange,
}: {
  locale: string;
  storeSlug: string;
  helpOpen: boolean;
  onHelpOpenChange: (open: boolean) => void;
  onHelpAnchorChange: (anchor: string | null) => void;
}) {
  const owner = useFloatingHelperOwner({ locale, storeSlug });
  return (
    <>
      {/* Always MOUNTED, not conditionally rendered: it draws nothing once
          setup is done, but it also owns the "your store is ready" celebration,
          which fires at exactly the moment the last step completes — the moment
          a swap would have unmounted it. */}
      <SetupLauncher locale={locale} storeSlug={storeSlug} />
      {owner !== "help" ? null : (
        <HelpLauncher
          storeSlug={storeSlug}
          open={helpOpen}
          onOpenChange={onHelpOpenChange}
          onAnchorChange={onHelpAnchorChange}
        />
      )}
    </>
  );
}
